// 2D canvas preview of laser files with pan (drag), zoom (wheel) and a playhead.
import type { LaserResult } from "../geom/types";

export interface Layer {
  res: LaserResult;
  color: string;
}

export class Preview2D {
  private ctx: CanvasRenderingContext2D;
  private layers: Layer[] = [];
  /** px per inch, and the page point at canvas origin */
  private scale = 20;
  private ox = 0;
  private oy = 0;
  private dpr = 1;
  private raf = 0;
  playhead: { x: number; y: number } | null = null;
  onHover?: (x: number, y: number) => void;

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext("2d")!;
    new ResizeObserver(() => this.resize()).observe(canvas);
    // one pointer pans, two pointers pinch-zoom around their midpoint (touch)
    const pts = new Map<number, { x: number; y: number }>();
    let drag: { x: number; y: number; ox: number; oy: number } | null = null;
    let pinch: { d: number; scale: number; px: number; py: number } | null = null;
    const local = (x: number, y: number) => {
      const r = canvas.getBoundingClientRect();
      return { x: x - r.left, y: y - r.top };
    };
    const startGesture = () => {
      const p = [...pts.values()];
      if (p.length === 1) {
        drag = { x: p[0].x, y: p[0].y, ox: this.ox, oy: this.oy };
        pinch = null;
      } else if (p.length >= 2) {
        drag = null;
        const m = local((p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2);
        pinch = { d: Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y) || 1, scale: this.scale, px: this.ox + m.x / this.scale, py: this.oy + m.y / this.scale };
      }
    };
    canvas.addEventListener("pointerdown", (e) => {
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      canvas.setPointerCapture(e.pointerId);
      canvas.classList.add("dragging");
      startGesture();
    });
    canvas.addEventListener("pointermove", (e) => {
      if (pts.has(e.pointerId)) pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch && pts.size >= 2) {
        const p = [...pts.values()];
        const m = local((p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2);
        const d = Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y);
        this.scale = Math.min(1e5, Math.max(2, pinch.scale * (d / pinch.d)));
        this.ox = pinch.px - m.x / this.scale;
        this.oy = pinch.py - m.y / this.scale;
        this.draw();
        return;
      }
      if (drag) {
        this.ox = drag.ox - (e.clientX - drag.x) / this.scale;
        this.oy = drag.oy - (e.clientY - drag.y) / this.scale;
        this.draw();
      }
      const l = local(e.clientX, e.clientY);
      this.onHover?.(this.ox + l.x / this.scale, this.oy + l.y / this.scale);
    });
    const end = (e: PointerEvent) => {
      pts.delete(e.pointerId);
      if (pts.size) startGesture();
      else { drag = null; pinch = null; canvas.classList.remove("dragging"); }
    };
    canvas.addEventListener("pointerup", end);
    canvas.addEventListener("pointercancel", end);
    canvas.addEventListener("wheel", (e) => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      const px = this.ox + mx / this.scale, py = this.oy + my / this.scale;
      this.scale = Math.min(1e5, Math.max(2, this.scale * Math.exp(-e.deltaY * 0.0015)));
      this.ox = px - mx / this.scale;
      this.oy = py - my / this.scale;
      this.draw();
    }, { passive: false });
    canvas.addEventListener("dblclick", () => this.fit());
  }

  setLayers(layers: Layer[], refit: boolean) {
    this.layers = layers;
    if (refit) this.fit();
    else this.draw();
  }

  private resize() {
    this.dpr = window.devicePixelRatio || 1;
    const r = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.max(1, Math.round(r.width * this.dpr));
    this.canvas.height = Math.max(1, Math.round(r.height * this.dpr));
    this.draw();
  }

  fit() {
    const r = this.canvas.getBoundingClientRect();
    const res = this.layers[0]?.res;
    if (!res || r.width === 0) return;
    const { w, h } = res.page;
    this.scale = Math.min(r.width / (w * 1.04), r.height / (h * 1.04));
    this.ox = w / 2 - r.width / 2 / this.scale;
    this.oy = h / 2 - r.height / 2 / this.scale;
    this.draw();
  }

  /** Zoom to the outer groove so the audio wiggles are visible. */
  grooveCloseUp() {
    const res = this.layers[0]?.res;
    if (!res) return;
    const r = this.canvas.getBoundingClientRect();
    const t = res.track;
    let x: number, y: number;
    if (t.kind === "polar") {
      // a point a quarter way into the audio, on the outer grooves
      const i = Math.floor(t.xy.length / 2 / 8) * 2;
      x = t.xy[i]; y = t.xy[i + 1];
    } else if (t.kind === "linear") {
      x = t.rows[0][Math.floor(t.rows[0].length / 4) & ~1];
      y = t.centers[0];
    } else return;
    this.scale = 3000;
    this.ox = x - r.width / 2 / this.scale;
    this.oy = y - r.height / 2 / this.scale;
    this.draw();
  }

  draw() {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.render());
  }

  private render() {
    const { ctx, dpr } = this;
    const W = this.canvas.width, H = this.canvas.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#0b0c0e";
    ctx.fillRect(0, 0, W, H);
    if (!this.layers.length) return;
    const s = this.scale * dpr;
    const tx = (x: number) => (x - this.ox) * s;
    const ty = (y: number) => (y - this.oy) * s;
    const page = this.layers[0].res.page;
    ctx.fillStyle = "#f4f1ea";
    ctx.fillRect(tx(0), ty(0), page.w * s, page.h * s);
    // inch grid when zoomed in enough
    if (this.scale > 40) {
      ctx.strokeStyle = "rgba(0,0,0,0.06)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      const step = this.scale > 2000 ? 0.01 : this.scale > 300 ? 0.1 : 1;
      const x0 = Math.max(0, Math.floor(this.ox / step) * step), x1 = Math.min(page.w, this.ox + W / s);
      const y0 = Math.max(0, Math.floor(this.oy / step) * step), y1 = Math.min(page.h, this.oy + H / s);
      for (let x = x0; x <= x1; x += step) { ctx.moveTo(tx(x), ty(0)); ctx.lineTo(tx(x), ty(page.h)); }
      for (let y = y0; y <= y1; y += step) { ctx.moveTo(tx(0), ty(y)); ctx.lineTo(tx(page.w), ty(y)); }
      ctx.stroke();
    }
    const lw = Math.max(1, Math.min(3, this.scale / 800)) * dpr;
    for (const layer of this.layers) {
      ctx.lineWidth = lw;
      for (const file of layer.res.files) {
        ctx.strokeStyle = layer.color;
        for (const p of file.paths) this.polyline(p, tx, ty, W, H);
        ctx.strokeStyle = "#111";
        for (const c of file.circles) {
          ctx.beginPath();
          ctx.arc(tx(c.cx), ty(c.cy), c.r * s, 0, Math.PI * 2);
          ctx.stroke();
        }
        if (file.boundingBox) ctx.strokeRect(tx(0), ty(0), page.w * s, page.h * s);
      }
    }
    if (this.playhead) {
      ctx.fillStyle = "#00c46a";
      ctx.beginPath();
      ctx.arc(tx(this.playhead.x), ty(this.playhead.y), 6 * dpr, 0, Math.PI * 2);
      ctx.fill();
    }
    // scale bar
    ctx.fillStyle = "#cfd3d9";
    ctx.font = `${11 * dpr}px system-ui`;
    const inches = [0.001, 0.01, 0.1, 1, 10].find((v) => v * this.scale > 60) ?? 10;
    ctx.fillRect(12 * dpr, H - 16 * dpr, inches * s, 2 * dpr);
    ctx.fillText(`${inches}″ = ${(inches * 25.4).toPrecision(3)} mm`, 12 * dpr, H - 22 * dpr);
  }

  /** Draws a long polyline, skipping sub-pixel steps and off-screen runs. */
  private polyline(p: Float32Array, tx: (x: number) => number, ty: (y: number) => number, W: number, H: number) {
    const ctx = this.ctx;
    ctx.beginPath();
    let lx = NaN, ly = NaN, drawing = false;
    const m = 4;
    for (let i = 0; i < p.length; i += 2) {
      const x = tx(p[i]), y = ty(p[i + 1]);
      const inside = x > -m && x < W + m && y > -m && y < H + m;
      if (!drawing) {
        if (inside || (i + 2 < p.length && this.inside(tx(p[i + 2]), ty(p[i + 3]), W, H))) {
          ctx.moveTo(x, y);
          lx = x; ly = y;
          drawing = true;
        }
        continue;
      }
      if (Math.abs(x - lx) + Math.abs(y - ly) < 0.6 && i + 2 < p.length) continue;
      ctx.lineTo(x, y);
      lx = x; ly = y;
      if (!inside) drawing = false;
    }
    ctx.stroke();
  }

  private inside(x: number, y: number, W: number, H: number) {
    return x > -4 && x < W + 4 && y > -4 && y < H + 4;
  }
}
